<template>
  <div 
    class="bg-white rounded-xl shadow p-3 flex items-center h-16 relative transition-all duration-200 overflow-hidden min-w-[60px]"
    :style="{ width: isMinimized ? '60px' : '192px' }"
  >
    <div 
      class="bg-cosmicPurple/20 p-2 rounded-lg flex-shrink-0 cursor-pointer stats-button z-10" 
      @click="toggleMinimize"
    >
      <ClipboardList class="h-5 w-5 text-cosmicPurple" />
    </div>
    
    <div 
      class="flex items-center ml-3 overflow-hidden"
      :class="{ 'w-0': isMinimized }"
    >
      <div class="whitespace-nowrap">
        <h3 class="text-xs text-gray-500 font-medium">Backlog Tasks</h3>
        <p class="text-md font-semibold text-sm">
          <span v-if="curriculumStore.isLoading" class="text-gray-400 inline-flex items-center">
            <LoaderCircle class="h-4 w-4 animate-spin mr-1" />
            <span>Loading...</span>
          </span>
          <span v-else-if="curriculumStore.backlogCount === 0 && noBacklogData" class="text-gray-400">
            0
          </span>
          <span v-else class="fade-in">{{ curriculumStore.backlogCount }}</span>
        </p>
      </div>
    </div>
    
    <button 
      @click="toggleMinimize" 
      class="text-gray-300 hover:text-gray-600 absolute top-2 right-2 transition-all duration-200 fade-in"
      :class="{ 'opacity-0': isMinimized, 'opacity-100': !isMinimized }"
    >
      <MinusSquare class="h-4 w-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { ClipboardList, MinusSquare, LoaderCircle } from 'lucide-vue-next';
import { useCurriculumStore } from '@/stores/curriculum';

const curriculumStore = useCurriculumStore();
const isMinimized = ref(false);

// Check if we actually have backlog data
const noBacklogData = computed(() => {
  if (!curriculumStore.curriculum?.backlog) return true;
  return Object.keys(curriculumStore.curriculum.backlog).length === 0;
});

// Load state from localStorage on component mount
onMounted(() => {
  try {
    const storedSettings = localStorage.getItem('userSettings');
    if (storedSettings) {
      const settings = JSON.parse(storedSettings);
      isMinimized.value = settings.backlogStatsMinimized || false;
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error);
  }
});

// Save state to localStorage when it changes
watch(isMinimized, (newValue) => {
  try {
    // Get existing settings or initialize empty object
    const storedSettings = localStorage.getItem('userSettings');
    const settings = storedSettings ? JSON.parse(storedSettings) : {};
    
    // Update only the backlogStatsMinimized property
    settings.backlogStatsMinimized = newValue;
    
    // Save back to localStorage
    localStorage.setItem('userSettings', JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings to localStorage:', error);
  }
});

const toggleMinimize = () => {
  isMinimized.value = !isMinimized.value;
};
</script> 

<style scoped>
.w-0 {
  width: 0;
}
</style>